import React from "react";
import { Facebook, Youtube, Linkedin, Instagram, Mail, Phone, MapPin } from "lucide-react";
import { SITE } from "@/data/site";

const SOCIAL_ICONS = {
  facebook: Facebook,
  youtube: Youtube,
  linkedin: Linkedin,
  instagram: Instagram,
};

const FOOTER_SERVICES = [
  {
    title: "Financial Planning",
    items: ["Financial plan", "Retirement projection", "Income diversification", "Education funding"],
  },
  {
    title: "Investments",
    items: ["Investment objectives", "Asset allocation", "Investment management", "Portfolio monitoring"],
  },
  {
    title: "Estate Planning",
    items: ["Intergenerational transfer", "Wills & trusts", "Power of attorney", "Charitable giving"],
  },
  {
    title: "Risk Management",
    items: ["Life insurance", "Critical illness", "Long-term care", "Mortgage insurance"],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#050507] text-white pt-20 pb-10 border-t border-white/[0.05]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src="/sunrise-logo.png"
                alt="Sunrise Wealth Management"
                className="h-16 w-auto object-contain"
                data-testid="footer-logo"
              />
            </div>
            <p className="mt-4 text-sm text-white/65 leading-relaxed max-w-sm">
              An educational approach to building family wealth, peace of mind and financial freedom — for emerging affluent Canadians.
            </p>
            <div className="mt-6 space-y-3 text-sm text-white/75">
              <a href={`mailto:${SITE.contact.email}`} className="flex items-center gap-2.5 hover:text-[#F5A623] transition-colors">
                <Mail className="w-4 h-4 text-[#F5A623]" /> {SITE.contact.email}
              </a>
              <a href={`tel:${SITE.contact.phone}`} className="flex items-center gap-2.5 hover:text-[#F5A623] transition-colors">
                <Phone className="w-4 h-4 text-[#F5A623]" /> {SITE.contact.phone}
              </a>
              <p className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#F5A623] mt-0.5 shrink-0" /> {SITE.contact.address}
              </p>
            </div>
            <div className="mt-7 flex items-center gap-3">
              {SITE.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.icon];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    data-testid={`footer-social-${s.icon}`}
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#F5A623] hover:text-[#F5A623] transition-colors"
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {FOOTER_SERVICES.map((g) => (
              <div key={g.title}>
                <h4 className="text-xs uppercase tracking-[0.22em] text-[#F5A623] font-semibold">
                  {g.title}
                </h4>
                <ul className="mt-4 space-y-2.5 text-sm text-white/70">
                  {g.items.map((it) => (
                    <li key={it} className="hover:text-white transition-colors cursor-default">{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Sunrise Wealth Management. All rights reserved.</p>
          <p className="font-serif italic text-white/60">
            "Build wealth with clarity. Protect what matters."
          </p>
        </div>
      </div>
    </footer>
  );
}
